 import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalStorageKeys } from 'src/app/enum/local-storage-keys.enum';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { TokenExpirationService } from '../token/token-expiration.service';
import { TokenCleanupService } from '../token/token-cleanup.service';
import { AUTH_ROUTES } from 'src/app/constants/auth.constants';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private tidSubject = new BehaviorSubject<string | null>(null);
  private authInitializedSubject = new BehaviorSubject<boolean>(false);
  private accountSubject = new BehaviorSubject<AccountInfo | null>(null);
  private initializationPromise: Promise<void> | null = null;

  constructor(
    private msalService: MsalService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private tokenExpirationService: TokenExpirationService,
    private tokenCleanupService: TokenCleanupService
  ) {}

  public async initMsalInstance(): Promise<void> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._initMsalInstanceInternal();
    await this.initializationPromise;
  }

  private async _initMsalInstanceInternal(): Promise<void> {
    try {
      await this.msalService.instance.initialize();

      const response = await this.msalService.instance.handleRedirectPromise();

      if (response) {
        this.msalService.instance.setActiveAccount(response.account);
        this.loadUserData();
      } else {
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          this.msalService.instance.setActiveAccount(accounts[0]);
          this.loadUserData();
        }
      }

      // Marcar inicialización como completa incluso sin cuenta activa
      // Esto previene que componentes se queden esperando indefinidamente
      this.authInitializedSubject.next(true);
    } catch (error) {
      this.authInitializedSubject.next(false);
      this.initializationPromise = null;
      throw error;
    }
  }

  private loadUserData(): void {
    const account = this.msalService.instance.getActiveAccount();
    if (account && account.idToken) {
      try {
        const decodedToken: any = jwtDecode(account.idToken);
        const tid = decodedToken?.tid || null;
        this.tidSubject.next(tid);
        this.accountSubject.next(account);
      } catch (error) {
        this.tidSubject.next(null);
        this.accountSubject.next(null);
      }
    } else {
      this.tidSubject.next(null);
      this.accountSubject.next(null);
    }
  }

  isAuthenticated(): boolean {
    const account = this.msalService.instance.getActiveAccount();
    return !!account;
  }

  getTenantId(): Observable<string | null> {
    return this.tidSubject.asObservable();
  }

  onAuthInitialized(): Observable<boolean> {
    return this.authInitializedSubject.asObservable();
  }

  getUserProfile(): Observable<AccountInfo | null> {
    return this.accountSubject.asObservable();
  }

  public refreshUserProfile(): void {
    this.loadUserData();
  }

  async logout(popup = false): Promise<void> {
    try {
      const account = this.msalService.instance.getActiveAccount();
      const postLogoutRedirectUri = this.buildPostLogoutRedirectUri();

      this.authInitializedSubject.next(false);
      this.tidSubject.next(null);
      this.accountSubject.next(null);
      this.tokenCleanupService.clearAuthenticationData();

      if (account) {
        await (popup
          ? this.msalService.instance.logoutPopup({
              mainWindowRedirectUri: postLogoutRedirectUri,
              account,
            })
          : this.msalService.instance.logoutRedirect({
              postLogoutRedirectUri,
              account,
            }));
      } else {
        this.router.navigate([AUTH_ROUTES.LOGIN]);
      }
    } catch (error) {
      this.tokenCleanupService.clearAuthenticationData();
      this.authInitializedSubject.next(false);
      this.router.navigate([AUTH_ROUTES.LOGIN]);
    }
  }

  private buildPostLogoutRedirectUri(): string {
    return `${window.location.origin}${window.location.pathname}#${AUTH_ROUTES.LOGIN}`;
  }

  isTokenExpired(): boolean {
    return this.tokenExpirationService.isExpired();
  }
  isLoggedIn(): boolean {
    const hasToken = !!this.localStorageService.get(LocalStorageKeys.ACCESSTOKEN);
    const isExpired = this.tokenExpirationService.isExpired();
    return hasToken && !isExpired;
  }
}
